// client/src/hooks/useSupportUnreadCount.ts (TypeScript since ST-00216; behaviour unchanged)
//
// The sidebar's support-ticket unread badge (lexadapt-support platform).
//
// It is a background poll, so it must never keep a session alive on its own
// (ST-00215 review S5): an open — or hidden — tab polling every minute would
// otherwise refresh the 5-minute access cookie forever and never sign out.
//   - pauses while the page is hidden (usePolling) and catches up on return;
//   - never refreshes the session (supportPlatform.unreadCount);
//   - when the session has lapsed it stops, keeping the last count, until
//     something the user does refreshes the session (onSessionRefreshed) —
//     then it fetches straight away and resumes.
import { useCallback, useEffect, useRef, useState } from 'react';
import usePolling from './usePolling';
import { supportPlatform } from '../services/supportPlatform';
import { onSessionRefreshed } from '../services/authClient';
import type { SupportPlatformError } from '../types/clientShell';

export const UNREAD_POLL_MS = 60000;

export default function useSupportUnreadCount(): number {
  const [count, setCount] = useState(0);
  const [sessionLapsed, setSessionLapsed] = useState(false);
  const lapsedRef = useRef(false);
  const mountedRef = useRef(false);

  const fetchUnread = useCallback(() => {
    supportPlatform
      .unreadCount()
      .then((data) => {
        if (mountedRef.current) setCount(Number(data?.count) || 0);
      })
      .catch((err: SupportPlatformError | undefined) => {
        // Other failures are decoration-only: keep the last count, keep polling.
        if (mountedRef.current && err?.sessionExpired) {
          lapsedRef.current = true;
          setSessionLapsed(true);
        }
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const unsubscribe = onSessionRefreshed(() => {
      if (!mountedRef.current || !lapsedRef.current) return;
      lapsedRef.current = false;
      setSessionLapsed(false);
      fetchUnread();
    });
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [fetchUnread]);

  // Declared after the effect above so mountedRef is set before the first poll.
  usePolling(fetchUnread, UNREAD_POLL_MS, { enabled: !sessionLapsed, immediate: true });

  return count;
}
